import { createContext, use, useCallback, useMemo, useState, type ReactNode } from "react";
import styles from "./Toast.module.css";
import { cx } from "./cx.js";

/**
 * Confirmation that something happened, for actions whose result is off
 * screen: "34 transactions recategorised" after a rule is applied from a
 * dialog that has already closed.
 *
 * Never for errors that need a decision — those belong next to the thing that
 * failed, where the retry is.
 */

interface Toast {
  id: number;
  message: string;
  tone: "info" | "positive" | "danger";
}

interface ToastContextValue {
  show: (message: string, tone?: Toast["tone"]) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DISMISS_AFTER_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, tone: Toast["tone"] = "info") => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, tone }]);
    setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, DISMISS_AFTER_MS);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext value={value}>
      {children}
      {/* Polite, not assertive: a confirmation must not interrupt someone
          mid-sentence in a screen reader. */}
      <div className={styles.region} role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={cx(styles.toast, styles[toast.tone])}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext>
  );
}

export function useToast(): ToastContextValue {
  const context = use(ToastContext);
  if (!context) throw new Error("useToast must be used inside a ToastProvider");
  return context;
}
